import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Search, Loader2, ChevronDown, X, ShieldAlert, HelpCircle,
  CheckCircle2, Database, AlertTriangle, Sparkles, FileJson,
} from 'lucide-react'
import { nsApi } from '../services/api'
import NsLineLinkWizard from './NsLineLinkWizard'

// ─── NsRecordTypePicker ───────────────────────────────────────────────────────
//
// One control for the single question "which NetSuite record does this
// template create?".
//
// It replaces two controls that used to sit side by side: a <select> listing
// only the record types someone had already registered, and a separate
// "Add a NetSuite record type…" button opening its own modal. That split
// exposed our own bookkeeping — whether a type happens to be registered yet —
// as a decision the user had to make before they could even look for their
// record. A user who wants "Sales Order" should type "sales order" and find
// it, not first work out which of two different controls is the one that
// would know about it.
//
// So: one search field over everything. Types already set up are listed
// first and are instantly selectable; anything else in the account is listed
// below and gets set up inline, in place, when picked. The line-item
// question is delegated to NsLineLinkWizard exactly as before — this changes
// where that question is asked, not how it is answered.

const SEARCH_DEBOUNCE_MS = 250
const SEARCH_LIMIT = 40

function ProbeBadge({ rt }) {
  if (rt.probe_status === 'forbidden') {
    return (
      <span title="This role has no permission to read this record type" className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
        <ShieldAlert className="h-3 w-3" /> No permission
      </span>
    )
  }
  if (rt.probe_status === 'never_probed' || rt.probe_status === 'throttled' || rt.probe_status === 'error') {
    return (
      <span title="Not yet confirmed whether this record type holds data" className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
        <HelpCircle className="h-3 w-3" /> Not yet checked
      </span>
    )
  }
  return null
}

export default function NsRecordTypePicker({
  entityTypes = [],
  loading = false,
  loadError = '',
  value = '',
  onChange,
  onRegistered,
  disabled = false,
  // Optional non-record-type action pinned at the top of "Ready to use" —
  // e.g. "Continue with JSON", which skips live NetSuite detection entirely
  // in favor of an uploaded field-mapping file. It deliberately does not
  // change `value`/`selected`: the record type chosen here (or left at its
  // default) is unaffected by this being active.
  jsonOption = null,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [hiddenCount, setHiddenCount] = useState(0)
  const [showAll, setShowAll] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  // The record type chosen from the "not set up yet" section. Held here —
  // not committed as the template's record type — until its one-time schema
  // fetch has actually landed, so the wizard can never advance with a type
  // the backend would later resolve as unknown (and silently downgrade to
  // Purchase Order).
  const [pending, setPending] = useState(null)
  const [lineReady, setLineReady] = useState(false)
  const [lineConfig, setLineConfig] = useState(null)
  const [registering, setRegistering] = useState(false)
  const [registerError, setRegisterError] = useState('')

  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const probeTriggeredRef = useRef(false)

  const selected = useMemo(
    // Case-insensitive: the template stores the key lowercased, while an
    // admin-registered record type keeps its own case (e.g. salesOrder).
    () => entityTypes.find(t =>
      String(t.ns_entity_type_key ?? '').toLowerCase() === String(value ?? '').toLowerCase()
    ) || null,
    [entityTypes, value],
  )

  // Both identifiers matter: a generic config's ns_entity_type_key IS its
  // NetSuite record type key, but the two built-ins ('purchase_order',
  // 'vendor_bid') carry their real NetSuite key on header_record instead.
  // Matching on only one of them listed Purchase Order twice — once as
  // "ready", once as if it still needed setting up.
  const configuredKeys = useMemo(() => {
    const set = new Set()
    entityTypes.forEach(t => {
      if (t.ns_entity_type_key) set.add(String(t.ns_entity_type_key).toLowerCase())
      if (t.header_record) set.add(String(t.header_record).toLowerCase())
    })
    return set
  }, [entityTypes])

  const readyMatches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entityTypes
    return entityTypes.filter(t =>
      String(t.display_name || '').toLowerCase().includes(q) ||
      String(t.ns_entity_type_key || '').toLowerCase().includes(q) ||
      String(t.header_record || '').toLowerCase().includes(q)
    )
  }, [entityTypes, query])

  const catalogMatches = useMemo(
    () => results.filter(r => !configuredKeys.has(String(r.record_type_key || '').toLowerCase())),
    [results, configuredKeys],
  )

  // One flat list behind the two visible sections, so arrow keys move
  // through what the eye sees rather than resetting at each heading.
  const navigable = useMemo(() => ([
    ...readyMatches.map(t => ({ kind: 'ready', key: t.ns_entity_type_key, item: t })),
    ...catalogMatches.map(r => ({ kind: 'catalog', key: r.record_type_key, item: r })),
  ]), [readyMatches, catalogMatches])

  useEffect(() => { setActiveIndex(-1) }, [query, open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setSearching(true)
    setSearchError('')
    const t = setTimeout(() => {
      nsApi.searchRecordTypes(query, SEARCH_LIMIT, showAll)
        .then(res => {
          if (cancelled) return
          setResults(res.data?.record_types || [])
          setHiddenCount(res.data?.hidden_count || 0)
        })
        .catch(err => {
          if (cancelled) return
          setResults([])
          setHiddenCount(0)
          setSearchError(err?.response?.data?.error || 'Could not reach NetSuite to search record types.')
        })
        .finally(() => { if (!cancelled) setSearching(false) })
    }, SEARCH_DEBOUNCE_MS)
    return () => { cancelled = true; clearTimeout(t) }
  }, [open, query, showAll])

  // Best-effort nudge for the background "has data" sweep, once per open —
  // bounded to a single batch server-side. Never blocks the picker, which
  // always has the cached catalog to show regardless.
  useEffect(() => {
    if (!open || probeTriggeredRef.current) return
    probeTriggeredRef.current = true
    nsApi.triggerRecordTypeProbe().catch(() => { /* best-effort only */ })
  }, [open])

  useEffect(() => {
    if (!open) { probeTriggeredRef.current = false; setShowAll(false); setQuery('') }
  }, [open])

  useEffect(() => {
    function handler(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const openPicker = useCallback(() => {
    if (disabled || registering) return
    setOpen(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [disabled, registering])

  const chooseReady = useCallback((entityType) => {
    setPending(null)
    setRegisterError('')
    onChange?.(entityType.ns_entity_type_key)
    setOpen(false)
  }, [onChange])

  const choosePending = useCallback((rt) => {
    setPending(rt)
    setLineReady(false)
    setLineConfig(null)
    setRegisterError('')
    setOpen(false)
  }, [])

  const cancelPending = useCallback(() => {
    setPending(null)
    setLineReady(false)
    setLineConfig(null)
    setRegisterError('')
  }, [])

  function handleKeyDown(e) {
    if (e.key === 'Escape') { setOpen(false); return }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!navigable.length) return
      setActiveIndex(i => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1
        if (next < 0) return navigable.length - 1
        if (next >= navigable.length) return 0
        return next
      })
      return
    }
    if (e.key === 'Enter') {
      const row = navigable[activeIndex]
      if (!row) return
      e.preventDefault()
      if (row.kind === 'ready') chooseReady(row.item)
      else choosePending(row.item)
    }
  }

  const commitPending = useCallback(() => {
    if (!pending || !lineReady || registering) return
    setRegistering(true)
    setRegisterError('')
    // Branch 1 (native embedded sublist, e.g. Purchase Order's own `item`)
    // reports { isEmbedded, lineContainerKey }; Branches 2/3 report a
    // separate, reference-linked line record. A null config means the
    // record genuinely has no lines.
    const payload = lineConfig?.isEmbedded ? {
      lineContainerKey: lineConfig.lineContainerKey,
      postMechanics: 'embedded',
    } : lineConfig ? {
      lineRecordTypeKey: lineConfig.lineRecordTypeKey,
      lineLinkField: lineConfig.lineLinkField,
      postMechanics: 'separate',
    } : undefined

    nsApi.createRecordType(pending.record_type_key, pending.display_label, payload)
      .then(res => {
        const data = res.data || {}
        if (!data.success) {
          setRegisterError(data.error || 'NetSuite would not accept this record type.')
          return
        }
        return Promise.resolve(onRegistered?.(pending.record_type_key)).then(types => {
          const landed = (types || []).some(t => t.ns_entity_type_key === pending.record_type_key)
          if (!landed) {
            // Never claim success when the thing the user actually cares
            // about — seeing it selected — did not happen.
            setRegisterError(
              `${pending.display_label} was saved but isn't available yet. Reopen this step in a moment to pick it.`
            )
            return
          }
          setPending(null)
          setLineReady(false)
          setLineConfig(null)
        })
      })
      .catch(err => {
        setRegisterError(
          err?.response?.data?.error || 'Could not set up this record type — NetSuite may be unreachable.'
        )
      })
      .finally(() => setRegistering(false))
  }, [pending, lineReady, lineConfig, registering, onRegistered])

  if (loading) {
    return <div className="h-[62px] rounded-lg bg-gray-100 dark:bg-gray-800/60 animate-pulse" />
  }

  return (
    <div className="space-y-2.5">
      <div ref={rootRef} className="relative rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-white dark:bg-gray-900">
        <label id="ns-record-type-label" className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1.5">
          Record Type
        </label>

        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || registering}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls="ns-record-type-listbox"
          aria-labelledby="ns-record-type-label"
          className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg border text-[13px] transition-colors ${
            open
              ? 'border-navy-500 ring-4 ring-navy-500/10 bg-white dark:bg-gray-800'
              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
          } ${disabled || registering ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span className="flex items-center gap-2 min-w-0">
            {jsonOption?.active ? (
              <FileJson className="h-3.5 w-3.5 text-[var(--brand-accent)] flex-shrink-0" />
            ) : (
              <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            )}
            {jsonOption?.active ? (
              <span className="truncate text-gray-900 dark:text-gray-100">{jsonOption.label || 'Continue with JSON'}</span>
            ) : selected ? (
              <span className="truncate text-gray-900 dark:text-gray-100">{selected.display_name}</span>
            ) : pending ? (
              <span className="truncate text-gray-500 dark:text-gray-400">{pending.display_label} — finish setup below</span>
            ) : (
              <span className="truncate text-gray-400">Search your NetSuite record types…</span>
            )}
          </span>
          <span className="flex items-center gap-2 flex-shrink-0">
            {selected?.header_record && (
              <span className="hidden sm:inline text-[10px] font-mono text-gray-400">{selected.header_record}</span>
            )}
            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </span>
        </button>

        {!open && !pending && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Choose which NetSuite record this template will create.
          </p>
        )}

        {open && (
          <div className="absolute z-[60] left-3 right-3 mt-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
            <div className="p-2 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a record name — invoice, sales order, bid…"
                  aria-autocomplete="list"
                  aria-controls="ns-record-type-listbox"
                  aria-activedescendant={activeIndex >= 0 ? `ns-rt-opt-${activeIndex}` : undefined}
                  className="flex-1 text-[13px] bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
                />
                {searching && <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin flex-shrink-0" />}
                {!searching && query && (
                  <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
                    <X className="h-3.5 w-3.5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            <div id="ns-record-type-listbox" role="listbox" aria-labelledby="ns-record-type-label" className="max-h-72 overflow-y-auto">
              {jsonOption && (
                <>
                  <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Ready to use</p>
                  <button
                    type="button"
                    role="option"
                    aria-selected={jsonOption.active}
                    onClick={() => { jsonOption.onSelect?.(); setOpen(false) }}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
                      jsonOption.active ? 'bg-[var(--brand-accent)]/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FileJson className="h-3.5 w-3.5 text-[var(--brand-accent)] flex-shrink-0" />
                      <span className="truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">{jsonOption.label || 'Continue with JSON'}</span>
                    </span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">Skip NetSuite connection</span>
                  </button>
                </>
              )}

              {readyMatches.length > 0 && (
                <>
                  {!jsonOption && <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Ready to use</p>}
                  {readyMatches.map((t, i) => (
                    <button
                      key={t.ns_entity_type_key}
                      id={`ns-rt-opt-${i}`}
                      type="button"
                      role="option"
                      aria-selected={t.ns_entity_type_key === value}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => chooseReady(t)}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
                        activeIndex === i ? 'bg-navy-50 dark:bg-navy-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                        <span className="truncate text-[13px] text-gray-900 dark:text-gray-100">{t.display_name}</span>
                      </span>
                      <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">{t.header_record || t.ns_entity_type_key}</span>
                    </button>
                  ))}
                </>
              )}

              {catalogMatches.length > 0 && (
                <>
                  <p className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    In your NetSuite account
                  </p>
                  {catalogMatches.map((r, i) => {
                    const idx = readyMatches.length + i
                    return (
                      <button
                        key={r.record_type_key}
                        id={`ns-rt-opt-${idx}`}
                        type="button"
                        role="option"
                        aria-selected={false}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => choosePending(r)}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
                          activeIndex === idx ? 'bg-navy-50 dark:bg-navy-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <Database className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate text-[13px] text-gray-900 dark:text-gray-100">{r.display_label}</span>
                          {r.is_custom_record && (
                            <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                              Custom
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          <ProbeBadge rt={r} />
                          <span className="text-[10px] font-mono text-gray-400">{r.record_type_key}</span>
                        </span>
                      </button>
                    )
                  })}
                </>
              )}

              {!searching && !navigable.length && (
                <p className="px-3 py-6 text-center text-[12px] text-gray-500 dark:text-gray-400">
                  {searchError || (query.trim()
                    ? `Nothing in NetSuite matches “${query.trim()}”.`
                    : 'No NetSuite record types available.')}
                </p>
              )}

              {!showAll && hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="w-full text-left px-3 py-2 border-t border-gray-100 dark:border-gray-800 text-[11px] text-navy-600 dark:text-navy-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Show all record types ({hiddenCount} hidden — confirmed empty)
                </button>
              )}
            </div>
          </div>
        )}

        {!open && loadError && !selected && !pending && (
          <div className="mt-2 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-gray-500 dark:text-gray-400">{loadError}</p>
          </div>
        )}
      </div>

      {/* Inline setup for a record type that hasn't been used here before.
          It stays in the flow rather than opening a modal on top of a modal:
          the user is answering one more question about the thing they just
          picked, not starting a separate admin task. */}
      {pending && (
        <div className="rounded-lg border border-navy-200 dark:border-navy-800 bg-navy-50/50 dark:bg-navy-900/10 px-3 py-3 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-navy-500 flex-shrink-0" />
                Setting up {pending.display_label}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                First time using this record here — we just need to know where its line items live.
              </p>
            </div>
            <button
              type="button"
              onClick={cancelPending}
              disabled={registering}
              className="p-1 rounded hover:bg-white/60 dark:hover:bg-gray-800 text-gray-400 disabled:opacity-40"
              aria-label="Cancel setup"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <NsLineLinkWizard
            key={pending.record_type_key}
            headerRecordType={pending}
            disabled={registering}
            onStateChange={({ ready, lineConfig: cfg }) => { setLineReady(ready); setLineConfig(cfg) }}
          />

          {registering && (
            <div className="flex items-start gap-2 rounded-lg bg-white dark:bg-gray-900 border border-navy-200 dark:border-navy-800 px-3 py-2.5">
              <Loader2 className="h-3.5 w-3.5 text-navy-500 animate-spin mt-0.5 flex-shrink-0" />
              <p className="text-[12px] text-navy-700 dark:text-navy-300">
                Reading {pending.display_label}'s field structure from NetSuite — this can take up to 2 minutes for a large record. Please keep this open.
              </p>
            </div>
          )}

          {registerError && !registering && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2.5">
              <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-[12px] text-red-600 dark:text-red-400">{registerError}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={cancelPending}
              disabled={registering}
              className="px-3 py-1.5 text-[12px] text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={commitPending}
              disabled={!lineReady || registering}
              className="px-3 py-1.5 text-[12px] font-medium bg-navy-600 hover:bg-navy-700 text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5"
            >
              {registering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Use this record type
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
